import { useEffect, useState } from "react"
import { Button, Checkbox, Flex, Modal, Spin } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Text } from "ui/Typography"
import AddNewFeeModal from "pagesV2/pro/Settings/TaxesAndFees/modal/AddNewFee"
import { getCustomFees, createCustomFee } from "store/custom-fees"
import { spacings } from "themes/variables"
import styles from "./AddTaxFeeModal.module.css"

const FEE_TYPE = { FIXED: "fixed", PERCENTAGE: "percentage" }

const formatFeeAmount = (fee) => {
  if (fee.fee_type === FEE_TYPE.PERCENTAGE) return `${fee.price}%`
  return `$${fee.price}`
}

const AddTaxFeeModal = ({ open, onCancel, onAdd }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [selectedIds, setSelectedIds] = useState([])
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const fees = useSelector((state) => state.customFees?.items || [])
  const isLoading = useSelector((state) => state.customFees?.loading || false)
  const isCreating = useSelector((state) => state.customFees?.createCustomFee?.loading || false)

  useEffect(() => {
    if (open) dispatch(getCustomFees())
  }, [open, dispatch])

  const toggleFee = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]))
  }

  const handleCreateFee = async (values) => {
    const feeData = {
      title: values.title,
      price: Number.parseFloat(values.price),
      fee_type: values.fee_type,
    }
    const result = await dispatch(createCustomFee(feeData))
    // After creation refresh the list and pre-select the new fee
    await dispatch(getCustomFees())
    if (result?.id) setSelectedIds((prev) => [...prev, result.id])
    setIsCreateModalOpen(false)
  }

  const handleAdd = () => {
    const selected = fees.filter((f) => selectedIds.includes(f.id))
    onAdd(selected)
    setSelectedIds([])
  }

  const handleCancel = () => {
    setSelectedIds([])
    onCancel()
  }

  const footer = (
    <Flex gap={spacings.S}>
      <Button shape="round" color="primary" variant="outlined" size="large" onClick={handleCancel}>
        {t("experiences.pricing.taxes_and_fees.cancel")}
      </Button>
      <Button
        shape="round"
        color="primary"
        variant="solid"
        size="large"
        disabled={selectedIds.length === 0}
        onClick={handleAdd}
      >
        {t("experiences.pricing.taxes_and_fees.add_selected", { count: selectedIds.length })}
      </Button>
    </Flex>
  )

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCancel}
        title={t("experiences.pricing.taxes_and_fees.modal_title")}
        width={800}
        footer={footer}
      >
        <Flex vertical gap={spacings.M}>
          <Flex align="center" gap={spacings.M}>
            <Text className={styles.subheaderText}>
              {t("experiences.pricing.taxes_and_fees.select_or_create")}
            </Text>
            <Button
              type="primary"
              shape="round"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              {t("experiences.pricing.taxes_and_fees.create_new")}
            </Button>
          </Flex>

          {isLoading ? (
            <Flex justify="center" style={{ padding: spacings.XL }}>
              <Spin />
            </Flex>
          ) : (
            <Flex vertical gap={spacings.XS} className={styles.feeList}>
              {fees.filter((f) => f.is_active).map((fee) => {
                const isSelected = selectedIds.includes(fee.id)
                return (
                  <Flex
                    key={fee.id}
                    align="center"
                    gap={spacings.S}
                    className={`${styles.feeItem} ${isSelected ? styles.feeItemSelected : ""}`}
                    onClick={() => toggleFee(fee.id)}
                  >
                    <Text className={styles.feeName}>{fee.title}</Text>
                    <Text className={styles.feeAmount}>{formatFeeAmount(fee)}</Text>
                    <Checkbox checked={isSelected} onChange={() => toggleFee(fee.id)} onClick={(e) => e.stopPropagation()} />
                  </Flex>
                )
              })}
            </Flex>
          )}
        </Flex>
      </Modal>

      <AddNewFeeModal
        visible={isCreateModalOpen}
        isSaving={isCreating}
        onCancel={() => setIsCreateModalOpen(false)}
        onAdd={handleCreateFee}
      />
    </>
  )
}

AddTaxFeeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
}

export default AddTaxFeeModal
